import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';

import { AlertsModule } from '@/alerts';
import { AssetCommunicationPage } from '@/communication/pages/asset-communication';
import { DataExplorerPage } from '@/data-explorer/pages/data-explorer';
import { SiteDocumentsPage } from '@/documents/view/pages/site-documents';
import { PageUrlKey } from '@/entities/page-url-key';
import { DataExplorerPageV2 } from '@/modules/data-explorer-v2/pages/data-explorer-v2';
import { SystemLogsPage } from '@/modules/system-logs';
import { CapacityTestPage } from '@/pages/assets/capacity-test';
import { EditAssetPage } from '@/pages/assets/edit-asset';
import { AssetOverviewPage } from '@/pages/assets/overview';
import { PCSModule } from '@/pcs';
import { TimelinePage } from '@/project-management/pages/timeline';
import { WorkstreamsPage } from '@/project-management/pages/workstreams';
import { SiteMap } from '@/site-map/pages/site-map';
import { SiteviewPage } from '@/siteview/pages/siteview';
import { AssetTasks } from '@/task-manager/pages/asset-tasks/asset-tasks';
import { WarrantyPage } from '@/warranty-v3/pages/warranty';
import { WarrantyDetailsPage } from '@/warranty/view/pages/warranty-details';

import { Shell } from '../components/layout/shell';
import { useFeatureFlag } from '../hooks/use-feature-flag';
import { useMe } from '../hooks/use-me';
import { useWarrantyVersion } from '../hooks/use-warranty-version';
import { DetailsPage } from '../pages/assets/details';
import { WarrantyPageLegacy } from '../pages/assets/Warranty';

function WarrantyRouter() {
  const warrantyVersion = useWarrantyVersion();

  switch (warrantyVersion?.version) {
    case 'v3':
      return <WarrantyPage />;
    case 'v2':
      return <WarrantyDetailsPage />;
    case 'v1':
      return <WarrantyPageLegacy />;
    default:
      return null;
  }
}

export function RoutesAsset() {
  const featureFlag = useFeatureFlag();
  const { path } = useRouteMatch();
  const me = useMe();
  const warrantyVersion = useWarrantyVersion();

  return (
    <Switch>
      {me.config.type === 'mobility' ? (
        <Redirect exact from={path} to={`${path}/${PageUrlKey.ALERTS}`} />
      ) : featureFlag('landingpage') ? (
        <Redirect exact from={path} to={`${path}/${PageUrlKey.OVERVIEW}`} />
      ) : (
        <Redirect exact from={path} to={`${path}/${PageUrlKey.DOCUMENTS}`} />
      )}
      {featureFlag('records') && me.config.type !== 'mobility' ? (
        <Route
          exact
          path={`${path}/${PageUrlKey.DOCUMENTS}`}
          component={function DocumentsGateway() {
            return (
              <Shell>
                <SiteDocumentsPage />
              </Shell>
            );
          }}
        />
      ) : null}
      {me.config.type !== 'mobility' ? (
        <Route
          exact
          path={`${path}/${PageUrlKey.OVERVIEW}`}
          component={function OverviewGateway() {
            return (
              <Shell>
                <AssetOverviewPage />
              </Shell>
            );
          }}
        />
      ) : null}
      {me.config.type !== 'mobility' && featureFlag('recordsCreate') ? (
        <Route
          exact
          path={`${path}/${PageUrlKey.OVERVIEW}/edit`}
          component={function OverviewGateway() {
            return (
              <Shell>
                <EditAssetPage />
              </Shell>
            );
          }}
        />
      ) : null}
      {featureFlag('alerts') ? (
        <Route
          exact
          path={`${path}/${PageUrlKey.ALERTS}`}
          component={function AlertsGateway() {
            return (
              <Shell>
                <AlertsModule />
              </Shell>
            );
          }}
        />
      ) : null}
      {featureFlag('taskmanager') ? (
        <Route
          exact
          path={`${path}/${PageUrlKey.TASKS}`}
          component={function TasksGateway() {
            return (
              <Shell>
                <AssetTasks />
              </Shell>
            );
          }}
        />
      ) : null}
      {featureFlag('details') ? (
        <Route
          exact
          path={`${path}/${PageUrlKey.DETAILS}`}
          component={function DetailsGateway() {
            return (
              <Shell>
                <DetailsPage />
              </Shell>
            );
          }}
        />
      ) : null}
      {featureFlag('dataExplorer') ? (
        <Route
          exact
          path={`${path}/${PageUrlKey.DATA_EXPLORER}`}
          component={function DataExplorerGateway() {
            return (
              <Shell>
                {featureFlag('newDataExplorer') ? (
                  <DataExplorerPageV2 />
                ) : (
                  <DataExplorerPage />
                )}
              </Shell>
            );
          }}
        />
      ) : null}
      {featureFlag('sitemap') ? (
        <Route
          exact
          path={`${path}/${PageUrlKey.SITE_MAP}`}
          component={function () {
            return (
              <Shell>
                <SiteMap />
              </Shell>
            );
          }}
        />
      ) : null}
      {warrantyVersion ? (
        <Route
          exact
          path={`${path}/${PageUrlKey.WARRANTY}`}
          component={function WarrantyGateway() {
            return (
              <Shell>
                <WarrantyRouter />
              </Shell>
            );
          }}
        />
      ) : null}
      {featureFlag('pcs') ? (
        <Route
          exact
          path={`${path}/${PageUrlKey.PCS}`}
          component={function () {
            return (
              <Shell>
                <PCSModule />
              </Shell>
            );
          }}
        />
      ) : null}
      {featureFlag('capacityTest') ? (
        <Route
          exact
          path={[
            `${path}/${PageUrlKey.CAPACITY_TEST}`,
            `${path}/${PageUrlKey.CAPACITY_TEST}/new`,
          ]}
          component={function () {
            return (
              <Shell>
                <CapacityTestPage />
              </Shell>
            );
          }}
        />
      ) : null}
      {featureFlag('communication') ? (
        <Route
          exact
          path={`${path}/${PageUrlKey.COMMUNICATION}`}
          component={() => (
            <Shell>
              <AssetCommunicationPage />
            </Shell>
          )}
        />
      ) : null}
      {featureFlag('projectManagement') ? (
        <Redirect
          exact
          from={`${path}/${PageUrlKey.PROJECT}`}
          to={`${path}/${PageUrlKey.PROJECT}/timeline`}
        />
      ) : null}
      {featureFlag('projectManagement') ? (
        <Route
          exact
          path={`${path}/${PageUrlKey.PROJECT}/timeline`}
          component={function () {
            return (
              <Shell>
                <TimelinePage />
              </Shell>
            );
          }}
        />
      ) : null}
      {featureFlag('projectManagement') ? (
        <Route
          exact
          path={`${path}/${PageUrlKey.PROJECT}/workstreams`}
          component={function () {
            return (
              <Shell>
                <WorkstreamsPage />
              </Shell>
            );
          }}
        />
      ) : null}
      {featureFlag('siteview') ? (
        <Route
          exact
          path={`${path}/${PageUrlKey.SITEVIEW}`}
          component={function () {
            return (
              <Shell>
                <SiteviewPage />
              </Shell>
            );
          }}
        />
      ) : null}
      {featureFlag('systemLogs') && me.config.type !== 'mobility' ? (
        <Route
          exact
          path={`${path}/${PageUrlKey.SYSTEM_LOGS}`}
          component={function SystemLogsGateway() {
            return (
              <Shell>
                <SystemLogsPage />
              </Shell>
            );
          }}
        />
      ) : null}
      <Redirect to={path} />
    </Switch>
  );
}
